import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Input, Button, Space, Typography, Avatar } from 'antd';
import { SendOutlined, UserOutlined, RobotOutlined, CloseOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import authService from '@/services/authService';
import queryService from '@/services/queryService';
import { AdminUser } from '@/types/auth';
import { QueryMessage, SSEEvent, SSECompleteEvent, ChatMessageEvent, ChartContext } from '@/types/query';
import { QueryResultCard } from '@/components/QueryResultCard';
import { ChartRenderer, StatCardRenderer } from '@/components/ChartRenderer';

const { Text } = Typography;
const { TextArea } = Input;

const QueryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();

  const [messages, setMessages] = useState<QueryMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(urlSessionId ?? null);
  const [chartContext, setChartContext] = useState<ChartContext | null>(
    (location.state as any)?.chartContext ?? null
  );
  
  // Streaming state
  const [streamingPreChat, setStreamingPreChat] = useState('');
  const [streamingPostChat, setStreamingPostChat] = useState('');
  const [streamingPhase, setStreamingPhase] = useState<'idle' | 'pre_chat' | 'sql' | 'executing' | 'post_chat'>('idle');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // When we navigate ourselves during streaming, skip the session reload triggered by the URL change
  const skipNextSessionLoad = useRef(false);

  // Load admin user once
  useEffect(() => {
    const loadAdminUser = async () => {
      try {
        const response = await authService.verifyAdmin();
        if (response.success && response.adminUser) {
          setAdminUser(response.adminUser);
        }
      } catch (error) {
        console.error('Failed to load admin user:', error);
      }
    };
    loadAdminUser();
  }, []);

  // Load session messages whenever the URL sessionId changes
  useEffect(() => {
    // If we navigated here ourselves during an active stream, don't reload
    if (skipNextSessionLoad.current) {
      skipNextSessionLoad.current = false;
      return;
    }

    setMessages([]);
    setSessionId(urlSessionId ?? null);

    if (!urlSessionId) return;

    const loadSession = async () => {
      try {
        const sessionResponse = await queryService.getSession(urlSessionId);
        if (sessionResponse.success && sessionResponse.session) {
          setSessionId(sessionResponse.session.id);
          if (sessionResponse.messages && sessionResponse.messages.length > 0) {
            const loadedMessages = sessionResponse.messages.map((msg: any) => ({
              id: msg.id,
              type: msg.type,
              content: msg.content,
              timestamp: msg.timestamp,
              sql: msg.sql,
              rowCount: msg.rowCount,
              chartData: msg.chartData,
              statData: msg.statData
            }));
            setMessages(loadedMessages);
          }
        }
      } catch (error) {
        console.error('Failed to load session:', error);
        navigate('/query', { replace: true });
      }
    };

    loadSession();
  }, [urlSessionId]);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingPreChat, streamingPostChat]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const question = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    
    // Reset streaming state
    setStreamingPreChat('');
    setStreamingPostChat('');
    setStreamingPhase('idle');

    // Capture and clear chart context so it only applies to this message
    const attachedChartContext = chartContext;
    setChartContext(null);

    // Add question to messages immediately
    const tempQuestionId = `temp-q-${Date.now()}`;
    const questionMessage: QueryMessage = {
      id: tempQuestionId,
      type: 'question',
      content: question,
      timestamp: new Date().toISOString(),
      chartContext: attachedChartContext ?? undefined,
    };

    setMessages(prev => [...prev, questionMessage]);

    try {
      await queryService.streamQuery(
        question,
        sessionId,
        attachedChartContext,
        (event: SSEEvent) => {
          // Handle different event types
          if (event.type === 'session') {
            const newSessionId = event.data.sessionId;
            setSessionId(newSessionId);
            // Update URL to reflect the new session without adding to history.
            // Set the skip flag so the useEffect doesn't wipe our in-progress messages.
            if (!urlSessionId) {
              skipNextSessionLoad.current = true;
              navigate(`/query/${newSessionId}`, { replace: true, state: location.state });
            }
          } else if (event.type === 'question') {
            // Update question with real ID from server
            setMessages(prev => prev.map(msg => 
              msg.id === tempQuestionId 
                ? { ...msg, id: event.data.messageId, timestamp: event.data.timestamp }
                : msg
            ));
          } else if (event.type === 'chat_message') {
            const chatEvent = event.data as ChatMessageEvent;
            if (chatEvent.phase === 'pre_query') {
              setStreamingPreChat(chatEvent.content);
              setStreamingPhase('pre_chat');
            } else if (chatEvent.phase === 'post_query') {
              setStreamingPostChat(chatEvent.content);
              setStreamingPhase('post_chat');
            }
          } else if (event.type === 'sql_started') {
            setStreamingPhase('sql');
          } else if (event.type === 'query_executing') {
            setStreamingPhase('executing');
          }
        },
        (completeEvent: SSECompleteEvent) => {
          // Add complete answer to messages
          const answerMessage: QueryMessage = {
            id: completeEvent.messageId,
            type: 'answer',
            content: completeEvent.answer,
            timestamp: completeEvent.timestamp,
            sql: completeEvent.sql,
            rowCount: completeEvent.rowCount,
            chartData: completeEvent.chartData || undefined,
            statData: completeEvent.statData || undefined
          };

          setMessages(prev => [...prev, answerMessage]);
          
          // Reset streaming state
          setStreamingPreChat('');
          setStreamingPostChat('');
          setStreamingPhase('idle');
          setIsLoading(false);
        },
        (error: Error) => {
          console.error('Query error:', error);
          
          // Add error message
          const errorMessage: QueryMessage = {
            id: `error-${Date.now()}`,
            type: 'answer',
            content: `Sorry, an error occurred: ${error.message}`,
            timestamp: new Date().toISOString()
          };

          setMessages(prev => [...prev, errorMessage]);
          
          // Reset state
          setStreamingPreChat('');
          setStreamingPostChat('');
          setStreamingPhase('idle');
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('Failed to send query:', error);
      setIsLoading(false);
      setStreamingPreChat('');
      setStreamingPostChat('');
      setStreamingPhase('idle');
    }
  };


  const renderMessage = (message: QueryMessage) => {
    const isQuestion = message.type === 'question';
    
    if (isQuestion) {
      return (
        <div key={message.id} className="mb-6 w-full">
          <div className="bg-white border border-gray-200 rounded-xl w-full shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <Avatar
                size={36}
                src={adminUser?.image_url}
                className="bg-primary-500 shrink-0"
                icon={!adminUser?.image_url && <UserOutlined />}
              />
              <Text strong className="text-base text-gray-800">
                You
              </Text>
              <Text type="secondary" className="text-sm text-gray-400">
                {new Date(message.timestamp).toLocaleTimeString()}
              </Text>
            </div>

            <div className="pl-12">
              {/* Embedded chart context from dashboard */}
              {message.chartContext && (
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                  <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Chart context:</span>
                    <span className="text-xs text-gray-700 font-semibold">{message.chartContext.title}</span>
                  </div>
                  <div className="p-3" style={{ height: 180 }}>
                    {message.chartContext.chartData ? (
                      <ChartRenderer chartData={message.chartContext.chartData} height={150} />
                    ) : message.chartContext.statData ? (
                      <StatCardRenderer statData={message.chartContext.statData} />
                    ) : null}
                  </div>
                </div>
              )}

              <div className="text-base text-gray-800 leading-relaxed">
                {message.content}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return <QueryResultCard key={message.id} message={message} />;
  };


  const getPhaseMessage = () => {
    switch (streamingPhase) {
      case 'pre_chat':
        return '💭 Thinking...';
      case 'sql':
        return '🔍 Preparing query...';
      case 'executing':
        return '⚡ Running query...';
      case 'post_chat':
        return '📊 Analyzing results...';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="h-[calc(100vh-48px)] flex flex-col -m-6">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-8 px-6 flex justify-center">
          <div className="w-full max-w-3xl">
            {messages.map(renderMessage)}
          
            {/* Streaming/Loading indicator */}
            {isLoading && (
              <div className="mt-6 w-full p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="bg-secondary-500 shrink-0" icon={<RobotOutlined />} />
                  <Text strong className="text-base text-gray-800">
                    Beemi Admin
                  </Text>
                  <Text type="secondary" className="text-sm text-gray-400">
                    {getPhaseMessage()}
                  </Text>
                </div>
                
                <div className="pl-12">
                  {/* Pre-query chat */}
                  {streamingPreChat && (
                    <div className="text-base text-gray-800 leading-relaxed mb-4 prose prose-sm max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p className="whitespace-pre-wrap mb-2">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                          li: ({ children }) => <li className="mb-1">{children}</li>,
                          code: ({ inline, children, ...props }: any) => 
                            inline ? (
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                                {children}
                              </code>
                            ) : (
                              <code className="block bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto text-sm font-mono my-2" {...props}>
                                {children}
                              </code>
                            ),
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                        }}
                      >
                        {streamingPreChat}
                      </ReactMarkdown>
                      {streamingPhase === 'pre_chat' && <span className="animate-pulse">▊</span>}
                    </div>
                  )}
                  
                  {/* Fetching data loader - show when SQL is ready or executing */}
                  {(streamingPhase === 'sql' || streamingPhase === 'executing') && (
                    <div className="mt-4 p-6 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      <span className="text-blue-700 font-medium">
                        {streamingPhase === 'sql' ? 'Generating query...' : 'Fetching data...'}
                      </span>
                    </div>
                  )}
                  
                  {/* Post-query chat */}
                  {streamingPostChat && (
                    <div className="mt-4 text-base text-gray-800 leading-relaxed prose prose-sm max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p className="whitespace-pre-wrap mb-2">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                          li: ({ children }) => <li className="mb-1">{children}</li>,
                          code: ({ inline, children, ...props }: any) => 
                            inline ? (
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                                {children}
                              </code>
                            ) : (
                              <code className="block bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto text-sm font-mono my-2" {...props}>
                                {children}
                              </code>
                            ),
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                        }}
                      >
                        {streamingPostChat}
                      </ReactMarkdown>
                      {streamingPhase === 'post_chat' && <span className="animate-pulse">▊</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="p-8 border-t border-gray-200 flex justify-center">
          <div className="w-full max-w-4xl">
            {/* Chart context preview banner */}
            {chartContext && (
              <div className="mb-4 border border-blue-200 bg-blue-50 rounded-xl overflow-hidden">
                <div className="px-4 py-2 flex items-center justify-between border-b border-blue-200">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-blue-600">Asking about chart:</span>
                    <span className="text-sm font-semibold text-blue-800">{chartContext.title}</span>
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setChartContext(null)}
                    className="text-blue-400 hover:text-blue-600"
                  />
                </div>
                <div className="p-3" style={{ height: 160 }}>
                  {chartContext.chartData ? (
                    <ChartRenderer chartData={chartContext.chartData} height={130} />
                  ) : chartContext.statData ? (
                    <StatCardRenderer statData={chartContext.statData} />
                  ) : null}
                </div>
              </div>
            )}

            <div className="flex gap-3 items-end">
              <TextArea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask a question about your database... (e.g., 'How many active users do we have?')"
                autoSize={{ minRows: 2, maxRows: 6 }}
                className="text-lg rounded-xl flex-1 px-4 py-3"
                disabled={isLoading}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                loading={isLoading}
                className="h-14 w-14 flex items-center justify-center rounded-xl text-xl"
                size="large"
              />
            </div>
            
            {/* Suggested Questions */}
            {messages.length === 0 && !isLoading && (
              <div className="mt-4">
                <Text type="secondary" className="text-xs block mb-2">
                  💡 Try asking:
                </Text>
                <Space wrap>
                  {[
                    'How many active users do we have?',
                    'Show me user signups this week',
                    'What are the top 10 most played games?',
                    'Show me daily active users for the last 7 days'
                  ].map((suggestion, idx) => (
                    <Button 
                      key={idx} 
                      size="small" 
                      onClick={() => setInputValue(suggestion)}
                      className="text-xs"
                    >
                      {suggestion}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryPage;
